// Reportes PDF de Inteligencia (jsPDF, A4 vertical). Las fotos se recortan en un canvas: si el navegador no
// deja leer la foto (CORS), el PDF sale igual, sin esa imagen.
import { jsPDF } from 'jspdf';
import { photoUrl } from '../iaData';
import { loadImg } from '../entregas';

const AZUL = [37, 99, 235];
const GRIS = [107, 114, 128];
const TEXTO = [31, 41, 55];

// Recorte de una foto con sus recuadros -> dataURL JPEG (o null si no se pudo leer)
export async function imagenRecorte(photo, crop, boxes = [], anchoPx = 640) {
  try {
    const c = crop || { x1: 0, y1: 0, x2: photo.w, y2: photo.h };
    // La miniatura más chica que tenga al menos anchoPx en el recorte (la 4K solo si el recorte es muy chico)
    const im = await loadImg(await photoUrl(photo, { w: Math.ceil((anchoPx * (photo.w || 1920)) / Math.max(1, c.x2 - c.x1)) }));
    const sx = im.naturalWidth / photo.w, sy = im.naturalHeight / photo.h;
    const cw = c.x2 - c.x1, ch = c.y2 - c.y1;
    const W = anchoPx, H = Math.round((anchoPx * ch) / cw);
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.drawImage(im, c.x1 * sx, c.y1 * sy, cw * sx, ch * sy, 0, 0, W, H);
    ctx.lineWidth = Math.max(2, W / 220);
    for (const b of boxes) {
      ctx.strokeStyle = b.color || '#facc15';
      ctx.strokeRect(((b.x1 - c.x1) / cw) * W, ((b.y1 - c.y1) / ch) * H, ((b.x2 - b.x1) / cw) * W, ((b.y2 - b.y1) / ch) * H);
    }
    return { url: cv.toDataURL('image/jpeg', 0.82), ratio: H / W };
  } catch (e) {
    return null;
  }
}

// Documento con encabezado Lisual, saltos de página automáticos y pie con número de página
export function nuevoPdf({ titulo, obra, periodo, nota }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, M = 14;
  let y = 0;
  const encabezado = () => {
    doc.setFillColor(...AZUL);
    doc.rect(0, 0, W, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
    doc.text(titulo, M, 10);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
    doc.text(`${obra || ''}${obra && periodo ? ' · ' : ''}${periodo || ''}`, M, 16.5);
    doc.text('Lisual', W - M, 10, { align: 'right' });
    y = 30;
  };
  encabezado();
  const api = {
    doc, M, W,
    get y() { return y; },
    set y(v) { y = v; },
    espacio(h) { if (y + h > 280) { doc.addPage(); encabezado(); } },
    titulo(t) {
      api.espacio(12);
      doc.setTextColor(...TEXTO); doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
      doc.text(t, M, y); y += 6;
    },
    texto(t, { tam = 9.5, color = TEXTO, negrita = false } = {}) {
      doc.setFont('helvetica', negrita ? 'bold' : 'normal'); doc.setFontSize(tam); doc.setTextColor(...color);
      const lineas = doc.splitTextToSize(String(t), W - 2 * M);
      api.espacio(lineas.length * tam * 0.42 + 2);
      doc.text(lineas, M, y); y += lineas.length * tam * 0.42 + 1.5;
    },
    nota(t) { api.texto(t, { tam: 8, color: GRIS }); },
    // Tabla simple: cols = [{t:'Hora', w:20}], filas = [['08:00', ...]]
    tabla(cols, filas) {
      const fila = (vals, head) => {
        api.espacio(6.5);
        let x = M;
        if (head) { doc.setFillColor(243, 244, 246); doc.rect(M, y - 4.2, W - 2 * M, 6, 'F'); }
        doc.setFont('helvetica', head ? 'bold' : 'normal'); doc.setFontSize(8.5); doc.setTextColor(...TEXTO);
        vals.forEach((v, i) => { doc.text(doc.splitTextToSize(String(v ?? ''), cols[i].w - 2)[0] || '', x + 1, y); x += cols[i].w; });
        y += 6;
      };
      fila(cols.map((c) => c.t), true);
      filas.forEach((f) => fila(f, false));
      y += 2;
    },
    // Fila de imágenes (dataURLs de imagenRecorte) con leyenda debajo
    async imagenes(items, { alto = 38 } = {}) {
      const n = items.length;
      if (!n) return;
      const gap = 4, ancho = (W - 2 * M - gap * (n - 1)) / n;
      api.espacio(alto + 8);
      items.forEach((it, i) => {
        const x = M + i * (ancho + gap);
        if (it.img) {
          let w = ancho, h = ancho * it.img.ratio;
          if (h > alto) { h = alto; w = alto / it.img.ratio; }
          doc.addImage(it.img.url, 'JPEG', x, y, w, h, undefined, 'FAST');
        } else {
          doc.setDrawColor(229, 231, 235); doc.rect(x, y, ancho, alto * 0.6);
          doc.setFontSize(7.5); doc.setTextColor(...GRIS); doc.text('Foto no disponible', x + 2, y + 5);
        }
        doc.setFontSize(7.5); doc.setTextColor(...GRIS);
        doc.text(doc.splitTextToSize(it.leyenda || '', ancho)[0] || '', x, y + alto + 4);
      });
      y += alto + 11;
    },
    guardar(nombre) {
      if (nota) { api.espacio(14); y += 2; api.nota(nota); }
      const n = doc.getNumberOfPages();
      for (let i = 1; i <= n; i++) {
        doc.setPage(i); doc.setFontSize(7.5); doc.setTextColor(...GRIS);
        doc.text(`Generado por Lisual · página ${i} de ${n}`, W / 2, 292, { align: 'center' });
      }
      doc.save(nombre);
    },
  };
  return api;
}

export const nombreArchivo = (base, obra, rango) => `${base}-${String(obra || 'obra').replace(/[^\w-]+/g, '_')}-${rango.desde}${rango.hasta !== rango.desde ? `_a_${rango.hasta}` : ''}.pdf`;
